import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarClock,
  Check,
  Download,
  Landmark,
  Plus,
  RefreshCcw,
  Trash2,
  Wallet,
} from "lucide-react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import PageLayout from "@/components/layout/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createExpense,
  createRecurringTransaction,
  createRentPayment,
  fetchCashflowProjection,
  fetchProfitAndLoss,
  listExpenses,
  listProperties,
  listRecurringTransactions,
  listRentPayments,
  removeExpense,
  removeRecurringTransaction,
  removeRentPayment,
  updateRecurringTransaction,
  updateRentPayment,
} from "@/services/dataService";
import { formatCurrency, formatDate, toDateInputValue } from "@/lib/format";

function parseNumber(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const next = Number(value);
  return Number.isFinite(next) ? next : undefined;
}

function toTimestamp(value) {
  if (!value) return Date.now();
  const timestamp = new Date(`${value}T00:00:00`).getTime();
  return Number.isFinite(timestamp) ? timestamp : Date.now();
}

function downloadCsv(filename, headers, rows) {
  const escapeCell = (cell) => {
    const text = String(cell ?? "");
    return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };
  const content = [headers, ...rows]
    .map((row) => row.map(escapeCell).join(","))
    .join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const RANGE_PRESETS = [
  { id: "thisMonth", label: "This month" },
  { id: "last3", label: "Last 3 months" },
  { id: "ytd", label: "Year to date" },
  { id: "last12", label: "Last 12 months" },
];

function rangeForPreset(preset) {
  const now = new Date();
  const endDate = Date.now();
  if (preset === "thisMonth") {
    return {
      startDate: new Date(now.getFullYear(), now.getMonth(), 1).getTime(),
      endDate,
    };
  }
  if (preset === "last3") {
    return {
      startDate: new Date(now.getFullYear(), now.getMonth() - 2, 1).getTime(),
      endDate,
    };
  }
  if (preset === "ytd") {
    return { startDate: new Date(now.getFullYear(), 0, 1).getTime(), endDate };
  }
  return {
    startDate: new Date(now.getFullYear() - 1, now.getMonth() + 1, 1).getTime(),
    endDate,
  };
}

function SummaryCard({ label, value, tone, icon: Icon, hint }) {
  const toneClass =
    tone === "income"
      ? "text-emerald-600 dark:text-emerald-300"
      : tone === "expense"
        ? "text-rose-600 dark:text-rose-300"
        : "text-foreground";
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
        {Icon ? (
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        ) : null}
      </CardHeader>
      <CardContent>
        <p className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
          {value}
        </p>
        {hint ? (
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

const EMPTY_RECURRING_FORM = {
  kind: "expense",
  title: "",
  amount: "",
  category: "mortgage",
  propertyId: "",
  frequency: "monthly",
  dayOfMonth: "1",
  startDate: toDateInputValue(Date.now()),
  endDate: "",
  notes: "",
};

const CASHFLOW_HORIZONS = [30, 60, 90];
const EXPENSE_CATEGORIES = [
  { value: "maintenance", label: "Maintenance / repair" },
  { value: "credit card payment", label: "Credit card payment" },
  { value: "mortgage", label: "Mortgage" },
  { value: "insurance", label: "Insurance" },
  { value: "utilities", label: "Utilities" },
  { value: "property tax", label: "Property tax" },
  { value: "other", label: "Other" },
];

export default function Finance() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const [isExpenseOpen, setIsExpenseOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isRecurringOpen, setIsRecurringOpen] = useState(false);
  const [rangePreset, setRangePreset] = useState("ytd");
  const [reportPropertyId, setReportPropertyId] = useState("");
  const [transactionFilter, setTransactionFilter] = useState("all");
  const [cashflowHorizon, setCashflowHorizon] = useState(30);

  const [expenseForm, setExpenseForm] = useState({
    propertyId: "",
    title: "",
    amount: "",
    category: "other",
    date: toDateInputValue(Date.now()),
    notes: "",
  });

  const [paymentForm, setPaymentForm] = useState({
    propertyId: "",
    amount: "",
    status: "completed",
    paymentDate: toDateInputValue(Date.now()),
    notes: "",
    paymentMethod: "bank_transfer",
  });

  const [recurringForm, setRecurringForm] = useState(EMPTY_RECURRING_FORM);

  const range = useMemo(() => rangeForPreset(rangePreset), [rangePreset]);

  const propertiesQuery = useQuery({
    queryKey: ["properties", userId],
    queryFn: () => listProperties(),
    enabled: Boolean(userId),
  });

  const expensesQuery = useQuery({
    queryKey: ["expenses", userId],
    queryFn: () => listExpenses(),
    enabled: Boolean(userId),
  });

  const paymentsQuery = useQuery({
    queryKey: ["rentPayments", userId],
    queryFn: () => listRentPayments(),
    enabled: Boolean(userId),
  });

  const reportQuery = useQuery({
    queryKey: [
      "profitAndLoss",
      userId,
      range.startDate,
      range.endDate,
      reportPropertyId,
    ],
    queryFn: () =>
      fetchProfitAndLoss({
        startDate: range.startDate,
        endDate: range.endDate,
        propertyId: reportPropertyId || undefined,
      }),
    enabled: Boolean(userId),
  });

  const recurringQuery = useQuery({
    queryKey: ["recurring", userId],
    queryFn: () => listRecurringTransactions(),
    enabled: Boolean(userId),
  });

  const cashflowQuery = useQuery({
    queryKey: ["cashflowProjection", userId, cashflowHorizon],
    queryFn: () => fetchCashflowProjection(cashflowHorizon),
    enabled: Boolean(userId),
  });

  const invalidateFinance = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["expenses", userId] }),
      queryClient.invalidateQueries({ queryKey: ["rentPayments", userId] }),
      queryClient.invalidateQueries({ queryKey: ["profitAndLoss", userId] }),
      queryClient.invalidateQueries({ queryKey: ["recurring", userId] }),
      queryClient.invalidateQueries({
        queryKey: ["cashflowProjection", userId],
      }),
    ]);
  };

  const createExpenseMutation = useMutation({
    mutationFn: createExpense,
    onSuccess: async () => {
      await invalidateFinance();
      setIsExpenseOpen(false);
      setExpenseForm({
        propertyId: "",
        title: "",
        amount: "",
        category: "other",
        date: toDateInputValue(Date.now()),
        notes: "",
      });
    },
    onError: (error) =>
      toast({
        title: "Could not save expense",
        description: error.message,
        variant: "destructive",
      }),
  });

  const createPaymentMutation = useMutation({
    mutationFn: createRentPayment,
    onSuccess: async () => {
      await invalidateFinance();
      setIsPaymentOpen(false);
      setPaymentForm({
        propertyId: "",
        amount: "",
        status: "completed",
        paymentDate: toDateInputValue(Date.now()),
        notes: "",
        paymentMethod: "bank_transfer",
      });
    },
    onError: (error) =>
      toast({
        title: "Could not save payment",
        description: error.message,
        variant: "destructive",
      }),
  });

  const markPaidMutation = useMutation({
    mutationFn: (id) =>
      updateRentPayment({ id, status: "completed", paymentDate: Date.now() }),
    onSuccess: invalidateFinance,
    onError: (error) =>
      toast({
        title: "Could not update payment",
        description: error.message,
        variant: "destructive",
      }),
  });

  const deletePaymentMutation = useMutation({
    mutationFn: removeRentPayment,
    onSuccess: invalidateFinance,
    onError: (error) =>
      toast({
        title: "Could not delete payment",
        description: error.message,
        variant: "destructive",
      }),
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: removeExpense,
    onSuccess: invalidateFinance,
    onError: (error) =>
      toast({
        title: "Could not delete expense",
        description: error.message,
        variant: "destructive",
      }),
  });

  const createRecurringMutation = useMutation({
    mutationFn: createRecurringTransaction,
    onSuccess: async () => {
      await invalidateFinance();
      setIsRecurringOpen(false);
      setRecurringForm(EMPTY_RECURRING_FORM);
      toast({ title: "Recurring schedule created" });
    },
    onError: (error) =>
      toast({
        title: "Could not save schedule",
        description: error.message,
        variant: "destructive",
      }),
  });

  const toggleRecurringMutation = useMutation({
    mutationFn: ({ id, isActive }) =>
      updateRecurringTransaction({ id, isActive }),
    onSuccess: invalidateFinance,
    onError: (error) =>
      toast({
        title: "Could not update schedule",
        description: error.message,
        variant: "destructive",
      }),
  });

  const deleteRecurringMutation = useMutation({
    mutationFn: removeRecurringTransaction,
    onSuccess: invalidateFinance,
    onError: (error) =>
      toast({
        title: "Could not delete schedule",
        description: error.message,
        variant: "destructive",
      }),
  });

  const propertyMap = useMemo(
    () =>
      Object.fromEntries(
        (propertiesQuery.data || []).map((property) => [
          property._id,
          property.name || `${property.address}, ${property.city}`,
        ]),
      ),
    [propertiesQuery.data],
  );

  const transactions = useMemo(() => {
    const expenseRows = (expensesQuery.data || []).map((expense) => ({
      id: expense._id,
      key: `expense-${expense._id}`,
      type: "expense",
      title: expense.title,
      amount: Number(expense.amount || 0),
      date: expense.date,
      propertyId: expense.propertyId,
      status: expense.category,
      notes: expense.notes,
    }));

    const paymentRows = (paymentsQuery.data || []).map((payment) => ({
      id: payment._id,
      key: `payment-${payment._id}`,
      type: "income",
      title: payment.notes?.startsWith("Scheduled:")
        ? payment.notes.replace("Scheduled: ", "")
        : "Rent Payment",
      amount: Number(payment.amount || 0),
      date: payment.paymentDate,
      propertyId: payment.propertyId,
      status: payment.status,
      notes: payment.notes,
    }));

    let rows = [...expenseRows, ...paymentRows];
    if (transactionFilter === "income")
      rows = rows.filter((row) => row.type === "income");
    if (transactionFilter === "expense")
      rows = rows.filter((row) => row.type === "expense");
    if (transactionFilter === "pending") {
      rows = rows.filter(
        (row) => row.type === "income" && row.status === "pending",
      );
    }
    return rows.sort((a, b) => b.date - a.date);
  }, [expensesQuery.data, paymentsQuery.data, transactionFilter]);

  const report = reportQuery.data;
  const cashflow = cashflowQuery.data;

  // Day-by-day series for the running-balance chart: bars for what hits the
  // account each day, a line for the cumulative position since today.
  const cashflowSeries = useMemo(() => {
    if (!cashflow) return [];
    const dayMs = 24 * 60 * 60 * 1000;
    const series = [];
    let running = 0;
    let index = 0;
    const events = cashflow.events || [];
    for (let t = cashflow.windowStart; t <= cashflow.windowEnd; t += dayMs) {
      let inflow = 0;
      let outflow = 0;
      while (index < events.length && events[index].date < t + dayMs) {
        if (events[index].kind === "in") inflow += events[index].amount;
        else outflow += events[index].amount;
        index += 1;
      }
      running += inflow - outflow;
      series.push({
        label: new Date(t).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        in: inflow,
        out: -outflow,
        running,
      });
    }
    return series;
  }, [cashflow]);

  const cashflowShortfall =
    cashflow?.lowestPoint && cashflow.lowestPoint.runningNet < 0
      ? cashflow.lowestPoint
      : null;

  const exportTransactionsCsv = () => {
    downloadCsv(
      `highlander-transactions-${toDateInputValue(Date.now())}.csv`,
      [
        "Date",
        "Type",
        "Title",
        "Property",
        "Status/Category",
        "Amount",
        "Notes",
      ],
      transactions.map((row) => [
        toDateInputValue(row.date),
        row.type,
        row.title,
        propertyMap[row.propertyId] || "Portfolio",
        row.status,
        row.type === "income" ? row.amount : -row.amount,
        row.notes || "",
      ]),
    );
  };

  const exportReportCsv = () => {
    if (!report) return;
    downloadCsv(
      `highlander-pnl-${toDateInputValue(range.startDate)}-to-${toDateInputValue(range.endDate)}.csv`,
      ["Month", "Income", "Expenses", "Net"],
      report.monthly.map((row) => [
        row.label,
        row.income,
        row.expenses,
        row.net,
      ]),
    );
  };

  return (
    <PageLayout
      title="Finance"
      eyebrow="Money"
      subtitle="Cash flow, income, expenses, P&L, and automated recurring entries — your Excel replacement."
      onRefresh={() => {
        expensesQuery.refetch();
        paymentsQuery.refetch();
        reportQuery.refetch();
        recurringQuery.refetch();
        cashflowQuery.refetch();
      }}
      isRefreshing={
        expensesQuery.isFetching ||
        paymentsQuery.isFetching ||
        reportQuery.isFetching
      }
    >
      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          setSearchParams(value === "overview" ? {} : { tab: value }, {
            replace: true,
          })
        }
        className="space-y-6"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="recurring">Recurring</TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setIsExpenseOpen(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Expense
            </Button>
            <Button onClick={() => setIsPaymentOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Payment
            </Button>
          </div>
        </div>

        <TabsContent value="overview" className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            {RANGE_PRESETS.map((preset) => (
              <Button
                key={preset.id}
                size="sm"
                variant={rangePreset === preset.id ? "default" : "outline"}
                onClick={() => setRangePreset(preset.id)}
              >
                {preset.label}
              </Button>
            ))}
            <select
              value={reportPropertyId}
              onChange={(event) => setReportPropertyId(event.target.value)}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            >
              <option value="">All properties</option>
              {(propertiesQuery.data || []).map((property) => (
                <option key={property._id} value={property._id}>
                  {property.name || property.address}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              onClick={exportReportCsv}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export P&L
            </Button>
          </div>

          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Income (collected)"
              value={formatCurrency(report?.incomeTotal ?? 0)}
              tone="income"
              icon={ArrowUpRight}
              hint={`${report?.transactionCounts?.payments ?? 0} payments`}
            />
            <SummaryCard
              label="Pending rent"
              value={formatCurrency(report?.pendingIncome ?? 0)}
              icon={CalendarClock}
              hint={`${report?.transactionCounts?.pendingPayments ?? 0} awaiting collection`}
            />
            <SummaryCard
              label="Expenses"
              value={formatCurrency(report?.expenseTotal ?? 0)}
              tone="expense"
              icon={ArrowDownRight}
              hint={`${report?.transactionCounts?.expenses ?? 0} entries`}
            />
            <SummaryCard
              label="Net income"
              value={formatCurrency(report?.netIncome ?? 0)}
              tone={(report?.netIncome ?? 0) >= 0 ? "income" : "expense"}
              icon={Wallet}
            />
          </section>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Monthly cash flow</CardTitle>
            </CardHeader>
            <CardContent>
              {report?.monthly?.length ? (
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={report.monthly}
                      margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) =>
                          `$${Math.round(value / 1000)}k`
                        }
                      />
                      <ChartTooltip
                        formatter={(value, name) => [
                          formatCurrency(value),
                          name,
                        ]}
                        contentStyle={{
                          background: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 12,
                        }}
                      />
                      <Bar
                        dataKey="income"
                        name="Income"
                        fill="#10b981"
                        radius={[6, 6, 0, 0]}
                      />
                      <Bar
                        dataKey="expenses"
                        name="Expenses"
                        fill="#f43f5e"
                        radius={[6, 6, 0, 0]}
                      />
                      <Line
                        dataKey="net"
                        name="Net"
                        stroke="#0ea5e9"
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  No financial activity in this range yet.
                </p>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Profit & loss by property
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(report?.byProperty || []).map((row) => (
                    <div
                      key={row.propertyId ?? "unassigned"}
                      className="flex items-center justify-between rounded-xl border p-3"
                    >
                      <div>
                        <p className="text-sm font-medium">{row.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(row.income)} in ·{" "}
                          {formatCurrency(row.expenses)} out
                        </p>
                      </div>
                      <span
                        className={`font-semibold tabular-nums ${
                          row.net >= 0 ? "text-emerald-600" : "text-rose-600"
                        }`}
                      >
                        {formatCurrency(row.net)}
                      </span>
                    </div>
                  ))}
                  {!report?.byProperty?.length ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      No data in range.
                    </p>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Expenses by category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(report?.expensesByCategory || []).map((row) => {
                    const share = report.expenseTotal
                      ? Math.round((row.amount / report.expenseTotal) * 100)
                      : 0;
                    return (
                      <div key={row.category}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium capitalize">
                            {row.category}
                          </span>
                          <span className="tabular-nums text-muted-foreground">
                            {formatCurrency(row.amount)} · {share}%
                          </span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-muted">
                          <div
                            className="h-2 rounded-full bg-rose-400"
                            style={{ width: `${share}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {!report?.expensesByCategory?.length ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      No expenses in range.
                    </p>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="cashflow" className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            {CASHFLOW_HORIZONS.map((days) => (
              <Button
                key={days}
                size="sm"
                variant={cashflowHorizon === days ? "default" : "outline"}
                onClick={() => setCashflowHorizon(days)}
              >
                Next {days} days
              </Button>
            ))}
            <p className="text-sm text-muted-foreground">
              Mortgages, insurance, and bills lined up against expected rent.
            </p>
          </div>

          {cashflowShortfall ? (
            <Card className="border-rose-500/30 bg-rose-500/10">
              <CardContent className="flex items-start gap-3 p-4">
                <AlertTriangle
                  aria-hidden
                  className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-300"
                />
                <p className="text-sm leading-6 text-rose-700 dark:text-rose-200">
                  <span className="font-semibold">
                    Overdraft risk around {formatDate(cashflowShortfall.date)}.
                  </span>{" "}
                  Scheduled payments run{" "}
                  {formatCurrency(Math.abs(cashflowShortfall.runningNet))} ahead
                  of expected rent at the tightest point. Keep at least that
                  much in the account, move a due date, or chase late rent
                  below.
                </p>
              </CardContent>
            </Card>
          ) : null}

          {cashflow?.overdueRent?.count ? (
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  Late rent — {formatCurrency(cashflow.overdueRent.total)}{" "}
                  outstanding
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {cashflow.overdueRent.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col gap-2 rounded-xl border border-amber-500/30 p-3 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <p className="text-sm font-medium">
                          {item.tenantName || "Tenant"} ·{" "}
                          {item.propertyName || "Property"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Due {formatDate(item.dueDate)} · {item.daysOverdue}{" "}
                          day
                          {item.daysOverdue === 1 ? "" : "s"} late
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-300">
                          {formatCurrency(item.amount)}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          disabled={markPaidMutation.isPending}
                          onClick={() => markPaidMutation.mutate(item.id)}
                        >
                          <Check className="h-3.5 w-3.5" />
                          Mark paid
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}

          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label={`Expected in (${cashflowHorizon}d)`}
              value={formatCurrency(cashflow?.totalIn ?? 0)}
              tone="income"
              icon={ArrowUpRight}
              hint="Rent and scheduled income"
            />
            <SummaryCard
              label={`Going out (${cashflowHorizon}d)`}
              value={formatCurrency(cashflow?.totalOut ?? 0)}
              tone="expense"
              icon={Landmark}
              hint="Mortgage drafts, insurance, bills"
            />
            <SummaryCard
              label="Projected net"
              value={formatCurrency(cashflow?.net ?? 0)}
              tone={(cashflow?.net ?? 0) >= 0 ? "income" : "expense"}
              icon={Wallet}
            />
            <SummaryCard
              label="Tightest point"
              value={formatCurrency(cashflow?.lowestPoint?.runningNet ?? 0)}
              tone={cashflowShortfall ? "expense" : "income"}
              icon={CalendarClock}
              hint={
                cashflow?.lowestPoint
                  ? `Around ${formatDate(cashflow.lowestPoint.date)}`
                  : "No scheduled events yet"
              }
            />
          </section>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Running position, day by day
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Bars show what hits the account each day; the line is your
                cumulative position starting from today. Where the line dips
                below zero is where the account gets drafted before rent lands.
              </p>
            </CardHeader>
            <CardContent>
              {cashflowSeries.length ? (
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={cashflowSeries}
                      margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        stroke="hsl(var(--muted-foreground))"
                        interval="preserveStartEnd"
                        minTickGap={24}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) =>
                          `$${Math.round(value / 1000)}k`
                        }
                      />
                      <ChartTooltip
                        formatter={(value, name) => [
                          formatCurrency(Math.abs(value)),
                          name === "out"
                            ? "Out"
                            : name === "in"
                              ? "In"
                              : "Running net",
                        ]}
                        contentStyle={{
                          background: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 12,
                        }}
                      />
                      <ReferenceLine
                        y={0}
                        stroke="hsl(var(--muted-foreground))"
                        strokeDasharray="4 4"
                      />
                      <Bar
                        dataKey="in"
                        name="in"
                        fill="#10b981"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="out"
                        name="out"
                        fill="#f43f5e"
                        radius={[0, 0, 4, 4]}
                      />
                      <Area
                        dataKey="running"
                        name="running"
                        stroke="#0ea5e9"
                        strokeWidth={2.5}
                        fill="#0ea5e9"
                        fillOpacity={0.08}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  {cashflowQuery.isLoading
                    ? "Building your projection…"
                    : "Nothing scheduled yet. Set mortgage payments and due days on your properties, and add recurring bills, to build the projection."}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Every scheduled money event
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                In draft order. The running column is your cumulative position
                after each event.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(cashflow?.events || []).map((event) => (
                  <div
                    key={event.key}
                    className="flex items-center justify-between gap-3 rounded-xl border p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {event.title}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {formatDate(event.date)}
                        {event.propertyName &&
                        !event.title.includes(event.propertyName)
                          ? ` · ${event.propertyName}`
                          : ""}{" "}
                        · {event.subtitle}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-4">
                      <span
                        className={`font-semibold tabular-nums ${
                          event.kind === "in"
                            ? "text-emerald-600"
                            : "text-rose-600"
                        }`}
                      >
                        {event.kind === "in" ? "+" : "−"}
                        {formatCurrency(event.amount)}
                      </span>
                      <span
                        className={`hidden w-24 text-right text-xs tabular-nums sm:block ${
                          event.runningNet < 0
                            ? "text-rose-600"
                            : "text-muted-foreground"
                        }`}
                      >
                        {formatCurrency(event.runningNet)}
                      </span>
                    </div>
                  </div>
                ))}
                {!cashflow?.events?.length && !cashflowQuery.isLoading ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No upcoming money events found.
                  </p>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <Card>
            <CardHeader className="flex flex-col gap-3 space-y-0 md:flex-row md:items-center md:justify-between">
              <CardTitle className="text-base">All transactions</CardTitle>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: "all", label: "All" },
                  { id: "income", label: "Income" },
                  { id: "expense", label: "Expenses" },
                  { id: "pending", label: "Pending rent" },
                ].map((filter) => (
                  <Button
                    key={filter.id}
                    size="sm"
                    variant={
                      transactionFilter === filter.id ? "default" : "outline"
                    }
                    onClick={() => setTransactionFilter(filter.id)}
                  >
                    {filter.label}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportTransactionsCsv}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.key}
                    className="flex flex-col gap-2 rounded-xl border p-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="font-medium">{transaction.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {propertyMap[transaction.propertyId] ||
                          "Portfolio-level"}{" "}
                        · {formatDate(transaction.date)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          transaction.type === "income"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {transaction.status}
                      </Badge>
                      <span
                        className={`font-semibold tabular-nums ${
                          transaction.type === "income"
                            ? "text-emerald-600"
                            : "text-rose-600"
                        }`}
                      >
                        {transaction.type === "income" ? "+" : "-"}
                        {formatCurrency(transaction.amount)}
                      </span>
                      {transaction.type === "income" &&
                      transaction.status === "pending" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          disabled={markPaidMutation.isPending}
                          onClick={() =>
                            markPaidMutation.mutate(transaction.id)
                          }
                        >
                          <Check className="h-3.5 w-3.5" />
                          Mark paid
                        </Button>
                      ) : null}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                        aria-label="Delete transaction"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete "${transaction.title}"? This cannot be undone.`,
                            )
                          ) {
                            if (transaction.type === "income") {
                              deletePaymentMutation.mutate(transaction.id);
                            } else {
                              deleteExpenseMutation.mutate(transaction.id);
                            }
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {transactions.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No transactions yet.
                  </p>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recurring">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-base">Recurring schedules</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Rent, mortgage, utilities — posted automatically every cycle.
                </p>
              </div>
              <Button
                onClick={() => setIsRecurringOpen(true)}
                className="gap-2"
              >
                <RefreshCcw className="h-4 w-4" />
                New schedule
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(recurringQuery.data || []).map((item) => (
                  <div
                    key={item._id}
                    className="flex flex-col gap-2 rounded-xl border p-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{item.title}</p>
                        <Badge
                          variant={
                            item.kind === "income" ? "default" : "secondary"
                          }
                        >
                          {item.kind}
                        </Badge>
                        {!item.isActive ? (
                          <Badge variant="outline">paused</Badge>
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {propertyMap[item.propertyId] || "Portfolio-level"} ·{" "}
                        {item.frequency} on day {item.dayOfMonth} · next{" "}
                        {formatDate(item.nextRunAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`font-semibold tabular-nums ${
                          item.kind === "income"
                            ? "text-emerald-600"
                            : "text-rose-600"
                        }`}
                      >
                        {item.kind === "income" ? "+" : "-"}
                        {formatCurrency(item.amount)}
                      </span>
                      <Switch
                        checked={item.isActive}
                        onCheckedChange={(checked) =>
                          toggleRecurringMutation.mutate({
                            id: item._id,
                            isActive: checked,
                          })
                        }
                        aria-label="Toggle schedule"
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                        aria-label="Delete schedule"
                        onClick={() => {
                          if (
                            window.confirm(`Delete schedule "${item.title}"?`)
                          ) {
                            deleteRecurringMutation.mutate(item._id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {!recurringQuery.data?.length ? (
                  <div className="py-10 text-center">
                    <p className="text-sm font-medium">
                      No recurring schedules yet
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Add your rents and mortgages once — entries post
                      themselves each month.
                    </p>
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isExpenseOpen} onOpenChange={setIsExpenseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add expense</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Property</Label>
              <select
                value={expenseForm.propertyId}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    propertyId: event.target.value,
                  }))
                }
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Portfolio-level</option>
                {(propertiesQuery.data || []).map((property) => (
                  <option key={property._id} value={property._id}>
                    {property.name || property.address}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Title</Label>
              <Input
                value={expenseForm.title}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    title: event.target.value,
                  }))
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Amount</Label>
                <Input
                  type="number"
                  min="0"
                  value={expenseForm.amount}
                  onChange={(event) =>
                    setExpenseForm((prev) => ({
                      ...prev,
                      amount: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Category</Label>
                <select
                  value={expenseForm.category}
                  onChange={(event) =>
                    setExpenseForm((prev) => ({
                      ...prev,
                      category: event.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  {EXPENSE_CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(event) =>
                    setExpenseForm((prev) => ({
                      ...prev,
                      date: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input
                value={expenseForm.notes}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExpenseOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                createExpenseMutation.mutate({
                  propertyId: expenseForm.propertyId || undefined,
                  title: expenseForm.title.trim(),
                  amount: parseNumber(expenseForm.amount) || 0,
                  category: expenseForm.category.trim() || "business",
                  date: toTimestamp(expenseForm.date),
                  isRecurring: false,
                  notes: expenseForm.notes.trim() || undefined,
                })
              }
              disabled={
                createExpenseMutation.isPending ||
                !expenseForm.title.trim() ||
                !parseNumber(expenseForm.amount)
              }
            >
              {createExpenseMutation.isPending ? "Saving..." : "Save expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add rent payment</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Property</Label>
              <select
                value={paymentForm.propertyId}
                onChange={(event) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    propertyId: event.target.value,
                  }))
                }
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Select property</option>
                {(propertiesQuery.data || []).map((property) => (
                  <option key={property._id} value={property._id}>
                    {property.name || property.address}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Amount</Label>
                <Input
                  type="number"
                  min="0"
                  value={paymentForm.amount}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      amount: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <select
                  value={paymentForm.status}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      status: event.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  <option value="completed">completed</option>
                  <option value="pending">pending</option>
                  <option value="overdue">overdue</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Payment date</Label>
                <Input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      paymentDate: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Payment method</Label>
                <Input
                  value={paymentForm.paymentMethod}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      paymentMethod: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input
                  value={paymentForm.notes}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      notes: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                createPaymentMutation.mutate({
                  propertyId: paymentForm.propertyId,
                  amount: parseNumber(paymentForm.amount) || 0,
                  paymentDate: toTimestamp(paymentForm.paymentDate),
                  status: paymentForm.status,
                  paymentMethod: paymentForm.paymentMethod.trim() || undefined,
                  notes: paymentForm.notes.trim() || undefined,
                })
              }
              disabled={
                createPaymentMutation.isPending ||
                !paymentForm.propertyId ||
                !parseNumber(paymentForm.amount)
              }
            >
              {createPaymentMutation.isPending ? "Saving..." : "Save payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRecurringOpen} onOpenChange={setIsRecurringOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New recurring schedule</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Type</Label>
                <select
                  value={recurringForm.kind}
                  onChange={(event) =>
                    setRecurringForm((prev) => ({
                      ...prev,
                      kind: event.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  <option value="income">Income (rent)</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>
                  Property{recurringForm.kind === "income" ? " (required)" : ""}
                </Label>
                <select
                  value={recurringForm.propertyId}
                  onChange={(event) =>
                    setRecurringForm((prev) => ({
                      ...prev,
                      propertyId: event.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  <option value="">Portfolio-level</option>
                  {(propertiesQuery.data || []).map((property) => (
                    <option key={property._id} value={property._id}>
                      {property.name || property.address}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Title</Label>
              <Input
                placeholder={
                  recurringForm.kind === "income"
                    ? "Rent — 123 Main St"
                    : "Mortgage — 123 Main St"
                }
                value={recurringForm.title}
                onChange={(event) =>
                  setRecurringForm((prev) => ({
                    ...prev,
                    title: event.target.value,
                  }))
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Amount</Label>
                <Input
                  type="number"
                  min="0"
                  value={recurringForm.amount}
                  onChange={(event) =>
                    setRecurringForm((prev) => ({
                      ...prev,
                      amount: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Category</Label>
                <Input
                  value={recurringForm.category}
                  onChange={(event) =>
                    setRecurringForm((prev) => ({
                      ...prev,
                      category: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Day of month</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={recurringForm.dayOfMonth}
                  onChange={(event) =>
                    setRecurringForm((prev) => ({
                      ...prev,
                      dayOfMonth: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Frequency</Label>
                <select
                  value={recurringForm.frequency}
                  onChange={(event) =>
                    setRecurringForm((prev) => ({
                      ...prev,
                      frequency: event.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Starts</Label>
                <Input
                  type="date"
                  value={recurringForm.startDate}
                  onChange={(event) =>
                    setRecurringForm((prev) => ({
                      ...prev,
                      startDate: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Ends (optional)</Label>
                <Input
                  type="date"
                  value={recurringForm.endDate}
                  onChange={(event) =>
                    setRecurringForm((prev) => ({
                      ...prev,
                      endDate: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input
                value={recurringForm.notes}
                onChange={(event) =>
                  setRecurringForm((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRecurringOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                createRecurringMutation.mutate({
                  kind: recurringForm.kind,
                  propertyId: recurringForm.propertyId || undefined,
                  title: recurringForm.title.trim(),
                  amount: parseNumber(recurringForm.amount) || 0,
                  category:
                    recurringForm.category.trim() ||
                    (recurringForm.kind === "income" ? "rent" : "other"),
                  frequency: recurringForm.frequency,
                  dayOfMonth: parseNumber(recurringForm.dayOfMonth) || 1,
                  startDate: toTimestamp(recurringForm.startDate),
                  endDate: recurringForm.endDate
                    ? toTimestamp(recurringForm.endDate)
                    : undefined,
                  notes: recurringForm.notes.trim() || undefined,
                })
              }
              disabled={
                createRecurringMutation.isPending ||
                !recurringForm.title.trim() ||
                !parseNumber(recurringForm.amount) ||
                (recurringForm.kind === "income" && !recurringForm.propertyId)
              }
            >
              {createRecurringMutation.isPending
                ? "Saving..."
                : "Create schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
